import {
    IconLine,
    IconListDetails,
    IconPoint,
    IconPolygon,
    IconScissors,
    IconShape2,
    IconWaveSquare,
    type Icon,
} from "@tabler/icons-react";
import type * as GeoJSON from "geojson";
import * as THREE from "three";
import { Line2 } from "three/addons/lines/Line2.js";
import { LineGeometry } from "three/addons/lines/LineGeometry.js";
import { LineMaterial } from "three/addons/lines/LineMaterial.js";
import { CSS2DObject } from "three/examples/jsm/Addons.js";
import { proxy } from "valtio";
import type { App, FeatureCollection } from "./app";
import { CrossSection } from "./cross-section";
import type { Journal } from "./history";
import type { DragEvent, InputEvent, ViewerEvent } from "./input-events";
import { LOD } from "./lod";
import type { Prefs } from "./prefs";
import { Project } from "./project";
import { getTileExtent, mercatorToLonLat, Terrain, tileKey } from "./terrain";
import {
    humanArea,
    humanDistance,
    isEmptyObject,
    metersToScene,
    sceneToMeters,
    setArray,
    setRecord,
    throttle,
    triangulate,
    UNIT_Z,
} from "./utils";
import { FOV_RAD, LAYERS, Object3DHit, PointHit, Viewer } from "./viewer";

export const MEASUREMENT_COLOR = "#F2C94C";
const MEASUREMENT_HIGHLIGHT_COLOR = "#4EA7FC";
const CROSS_SECTION_START_COLOR = "#82DF30";
const CROSS_SECTION_END_COLOR = "#FF5C26";

export const FILL_OPACITY = 0.1;
export const POINT_SIZE = 12;
const SMALL_POINT_SIZE = 8;
const LARGE_POINT_SIZE = 16;

const HOVER_LINE_WIDTH = 4;
const LINE_WIDTH = 2;
const TARGET_DASH_SIZE = 8;
const CHUNK_SIZE = 1000;
const DEFAULT_THRESHOLD = 0.1;
const STRIPE_PERIOD = 100;

const STYLE_PREVIEW_MS = 2000;
const STYLE_KEYS = new Set([
    "marker-color",
    "marker-opacity",
    "marker-size",
    "stroke",
    "stroke-opacity",
    "fill",
    "fill-opacity",
]);

// Anti-aliased diagonal stripes, oriented relative to north (world Y) so the
// pattern stays locked to the ground rather than the screen. Reads a `vWorld`
// (world XY) varying and leaves `_coverage` in [0, 1] (1 on a stripe, 0 in the
// gap) for the caller to modulate alpha with.
function stripeCoverageGLSL(): string {
    return `
        float _diag = (vWorld.x + vWorld.y) / ${STRIPE_PERIOD.toFixed(1)};
        float _tri = abs(2.0 * fract(_diag) - 1.0);
        float _aa = fwidth(_diag) * 2.0;
        float _coverage = smoothstep(0.5 - _aa, 0.5 + _aa, _tri);
    `;
}

type InputState = "idle" | "drafting" | "dragging";

export type DraftType = "point" | "line" | "path" | "area";

// https://github.com/mapbox/simplestyle-spec/tree/master/1.1.0
type SimpleStyleCommon = { description?: string; hidden?: boolean; locked?: boolean };

type SimpleStyleStroke = { stroke?: string; "stroke-opacity"?: number; "stroke-width"?: number };

type SimpleStyleFill = { fill?: string; "fill-opacity"?: number };

// Marker opacity is not in the simplestyle spec but seems useful
type SimpleStyleMarker = {
    "marker-color"?: string;
    "marker-opacity"?: number;
    "marker-size"?: "small" | "medium" | "large";
};

export type PointFeature = GeoJSON.Feature<
    GeoJSON.Point,
    { title?: string | null; "dataset-id": string | null; label?: boolean } & SimpleStyleMarker & SimpleStyleCommon
>;

export type LineStringFeature = GeoJSON.Feature<
    GeoJSON.LineString,
    { title?: string | null; "cross-section"?: boolean; label?: boolean } & SimpleStyleMarker &
        SimpleStyleStroke &
        SimpleStyleCommon
>;

export type PolygonFeature = GeoJSON.Feature<
    GeoJSON.Polygon,
    { title?: string | null; crop?: boolean; label?: boolean } & SimpleStyleMarker &
        SimpleStyleStroke &
        SimpleStyleFill &
        SimpleStyleCommon
>;

export type PropertiesFeature = GeoJSON.Feature<null, { title?: string | null } & SimpleStyleCommon>;

export type Feature = (PointFeature | LineStringFeature | PolygonFeature | PropertiesFeature) & {
    collectionId?: string;
};

export type Outline = {
    id: string;
    color: string;
    boundary: GeoJSON.MultiPolygon;
    z: number;
};

type Chunk = {
    nodes: THREE.Points;
    lines: THREE.LineSegments;
    fills: THREE.Mesh;
    box: THREE.Box3;
};

export type FeatureParts = {
    points: THREE.Points;
    draftPoints: THREE.Points;
    line: Line2;
    fill: THREE.Mesh;
    auxLine: Line2;
    labels: CSS2DObject[];
};

// Overlap test for two [minLon, minLat, maxLon, maxLat] bboxes.
function bboxesIntersect(a: [number, number, number, number], b: [number, number, number, number]): boolean {
    return a[0] < b[2] && a[2] > b[0] && a[1] < b[3] && a[3] > b[1];
}

export function isPointFeature(f: Feature): f is PointFeature {
    return f.geometry?.type === "Point";
}

export function isLineStringFeature(f: Feature): f is LineStringFeature {
    return f.geometry?.type === "LineString";
}

export function isPolygonFeature(f: Feature): f is PolygonFeature {
    return f.geometry?.type === "Polygon";
}

type KeysOfUnion<T> = T extends unknown ? keyof T : never;
type PropertyOfUnion<T, K extends PropertyKey> = T extends unknown ? (K extends keyof T ? T[K] : never) : never;
export function getFeatureProperty<Key extends KeysOfUnion<Feature["properties"]>>(
    f: Feature,
    k: Key,
): PropertyOfUnion<Feature["properties"], Key> {
    return f.properties[k as keyof typeof f.properties] as PropertyOfUnion<Feature["properties"], Key>;
}

// Geometry-based kind. Drives measurement math and is the fallback for display.
export type FeatureKind = "point" | "line" | "path" | "area" | "properties";

// Display kind layers the `crop` / `cross-section` flags on top of geometry.
export type FeatureDisplayKind = FeatureKind | "cross-section" | "clip";

export function featureKind(f: Feature): FeatureKind {
    if (f.geometry === null) return "properties";
    if (f.geometry.type === "Point") return "point";
    if (f.geometry.type === "Polygon") return "area";
    return f.geometry.coordinates.length <= 2 ? "line" : "path";
}

export function featureDisplayKind(f: Feature): FeatureDisplayKind {
    if (f.geometry !== null && "crop" in f.properties) return "clip";
    if (f.geometry !== null && "cross-section" in f.properties) return "cross-section";
    return featureKind(f);
}

// Single source of truth for how a feature is shown in lists, titles, and draft help.
const FEATURE_DISPLAY: Record<FeatureDisplayKind, { name: string; Icon: Icon }> = {
    point: { name: "Point", Icon: IconPoint },
    line: { name: "Line", Icon: IconLine },
    path: { name: "Path", Icon: IconShape2 },
    area: { name: "Area", Icon: IconPolygon },
    "cross-section": { name: "Cross-section", Icon: IconWaveSquare },
    clip: { name: "Clip", Icon: IconScissors },
    properties: { name: "Properties", Icon: IconListDetails },
};

export function featureDisplay(f: Feature): { name: string; Icon: Icon } {
    return FEATURE_DISPLAY[featureDisplayKind(f)];
}

export function isFeatureLocked(feature: Feature | undefined, collections: Record<string, FeatureCollection>): boolean {
    if (!feature) {
        return true;
    }

    const collection = feature.collectionId ? collections[feature.collectionId] : undefined;
    return feature.properties?.locked === true || collection?.properties?.locked === true;
}

export function featurePositions(f: Feature): GeoJSON.Position[] {
    let coordinates: GeoJSON.Position[] = [];
    if (isPointFeature(f)) {
        if (f.geometry.coordinates.length > 0) {
            coordinates = [f.geometry.coordinates];
        }
    } else if (isLineStringFeature(f)) {
        coordinates = f.geometry.coordinates;
    } else if (isPolygonFeature(f)) {
        coordinates = f.geometry.coordinates[0];
    }

    return coordinates;
}

export function closedRing(geometry: Feature["geometry"]): Feature["geometry"] {
    if (geometry === null) return geometry;
    if (geometry.type !== "Polygon") return geometry;
    const ring = geometry.coordinates[0];
    if (ring.length < 3) return geometry;
    const first = ring[0];
    const last = ring[ring.length - 1];
    const isClosed = first[0] === last[0] && first[1] === last[1] && (first[2] ?? 0) === (last[2] ?? 0);
    if (isClosed) return geometry;
    return { type: "Polygon", coordinates: [[...ring, [...first]]] };
}

export function openedRing(geometry: Exclude<GeoJSON.Geometry, null>): Exclude<Feature["geometry"], null> {
    if (geometry.type !== "Polygon") {
        return geometry as Exclude<Feature["geometry"], null>;
    }

    const ring = geometry.coordinates[0] ?? [];
    if (ring.length > 1) {
        const first = ring[0];
        const last = ring[ring.length - 1];
        const isClosed = first[0] === last[0] && first[1] === last[1] && (first[2] ?? 0) === (last[2] ?? 0);
        if (isClosed) {
            return { type: "Polygon", coordinates: [ring.slice(0, -1)] };
        }
    }

    return geometry;
}

function setPosition(f: Feature, index: number, pos: GeoJSON.Position): void {
    if (isPointFeature(f)) {
        f.geometry.coordinates = pos;
    } else if (isLineStringFeature(f)) {
        f.geometry.coordinates[index] = pos;
    } else if (isPolygonFeature(f)) {
        f.geometry.coordinates[0][index] = pos;
    }
}

function pushPosition(f: Feature, pos: GeoJSON.Position): void {
    if (isPointFeature(f)) {
        setArray(f.geometry.coordinates, pos);
    } else if (isLineStringFeature(f)) {
        f.geometry.coordinates.push(pos);
    } else if (isPolygonFeature(f)) {
        f.geometry.coordinates[0].push(pos);
    }
}

function popPosition(f: Feature): void {
    if (isPointFeature(f)) {
        f.geometry.coordinates.length = 0;
    } else if (isLineStringFeature(f)) {
        f.geometry.coordinates.pop();
    } else if (isPolygonFeature(f)) {
        f.geometry.coordinates[0].pop();
    }
}

function spliceCoord(f: Feature, index: number, count: number): void {
    if (isLineStringFeature(f)) {
        f.geometry.coordinates.splice(index, count);
    } else if (isPolygonFeature(f)) {
        f.geometry.coordinates[0].splice(index, count);
    }
}

function patchPointFragmentShader(shader: Parameters<THREE.Material["onBeforeCompile"]>[0]): void {
    shader.fragmentShader = shader.fragmentShader.replace(
        "#include <alphatest_fragment>",
        `
        vec2 center = gl_PointCoord - vec2(0.5);
        float dist = length(center) * 2.0;
        float delta = fwidth(dist);

        float alpha = 1.0 - smoothstep(1.0 - delta, 1.0, dist);

        float inner_radius = 0.666;
        float white_dist = dist;
        float white_mask = 1.0 - smoothstep(inner_radius - delta, inner_radius, white_dist);

        diffuseColor.rgb = mix(diffuseColor.rgb, vec3(1.0), white_mask);
        diffuseColor.a *= alpha;

        #include <alphatest_fragment>
        `,
    );
}

const pointMaterials = new Map<string, THREE.PointsMaterial>();
export function getPointMaterial(size: number, opacity: number, color?: string): THREE.PointsMaterial {
    const key = `${size}-${opacity}${color ? `-${color}` : ""}`;
    const cached = pointMaterials.get(key);
    if (cached) return cached;

    const material = new THREE.PointsMaterial({
        color: color,
        size: size,
        sizeAttenuation: false,
        depthTest: false,
        transparent: true,
        opacity: opacity,
        vertexColors: color ? false : true,
    });

    material.onBeforeCompile = (shader) => {
        patchPointFragmentShader(shader);
    };

    pointMaterials.set(key, material);
    return material;
}

const lineMaterials = new Map<string, LineMaterial>();
function getLine2Material(
    color: string,
    linewidth: number,
    resolution: { width: number; height: number },
    opacity = 1,
    dashSize = 0,
): LineMaterial {
    const key = `${color}-${linewidth}-${dashSize}-${opacity}`;

    let material = lineMaterials.get(key);
    if (!material) {
        material = new LineMaterial({
            color: color,
            linewidth: linewidth,
            dashSize: dashSize,
            gapSize: dashSize,
            depthTest: false,
            dashed: dashSize > 0,
            transparent: opacity < 1,
            opacity,
        });
    }

    if (!material.resolution.equals({ x: resolution.width, y: resolution.height })) {
        material.resolution.set(resolution.width, resolution.height);
        material.needsUpdate = true;
    }

    lineMaterials.set(key, material);
    return material;
}

const fillMaterials = new Map<string, THREE.MeshBasicMaterial>();
function getFillMaterial(color: string, opacity = FILL_OPACITY): THREE.MeshBasicMaterial {
    const key = `${color}-${opacity}`;
    const cached = fillMaterials.get(key);
    if (cached) return cached;

    const material = new THREE.MeshBasicMaterial({
        color: color,
        transparent: opacity < 1,
        opacity,
        side: THREE.DoubleSide,
        depthTest: false,
    });

    fillMaterials.set(key, material);
    return material;
}

const cropFillMaterials = new Map<string, THREE.MeshBasicMaterial>();
function getCropFillMaterial(color: string, opacity = FILL_OPACITY): THREE.MeshBasicMaterial {
    const key = `${color}-${opacity}`;
    const cached = cropFillMaterials.get(key);
    if (cached) return cached;

    const material = new THREE.MeshBasicMaterial({
        color: color,
        transparent: true,
        opacity,
        side: THREE.DoubleSide,
        depthTest: false,
    });

    material.onBeforeCompile = (shader) => {
        shader.vertexShader =
            "varying vec2 vWorld;\n" +
            shader.vertexShader.replace(
                "#include <begin_vertex>",
                "#include <begin_vertex>\n    vWorld = (modelMatrix * vec4(position, 1.0)).xy;",
            );
        shader.fragmentShader =
            "varying vec2 vWorld;\n" +
            shader.fragmentShader.replace(
                "#include <alphatest_fragment>",
                `${stripeCoverageGLSL()}    diffuseColor.a *= _coverage;\n    #include <alphatest_fragment>`,
            );
    };

    cropFillMaterials.set(key, material);
    return material;
}

let sharedLineMaterial: THREE.LineBasicMaterial | null = null;
function getSharedLineMaterial(): THREE.LineBasicMaterial {
    if (sharedLineMaterial) return sharedLineMaterial;

    sharedLineMaterial = new THREE.LineBasicMaterial({
        vertexColors: true,
        transparent: true,
        depthTest: false,
    });

    // Per-feature stroke-opacity via a per-vertex `alpha` attribute.
    sharedLineMaterial.onBeforeCompile = (shader) => {
        shader.vertexShader =
            "attribute float alpha;\nvarying float vAlpha;\n" +
            shader.vertexShader.replace("#include <begin_vertex>", "#include <begin_vertex>\n    vAlpha = alpha;");
        shader.fragmentShader =
            "varying float vAlpha;\n" +
            shader.fragmentShader.replace(
                "#include <opaque_fragment>",
                "    diffuseColor.a *= vAlpha;\n    #include <opaque_fragment>",
            );
    };

    return sharedLineMaterial;
}

let sharedPointMaterial: THREE.PointsMaterial | null = null;
function getSharedPointMaterial(): THREE.PointsMaterial {
    if (sharedPointMaterial) return sharedPointMaterial;

    const material = new THREE.PointsMaterial({
        size: POINT_SIZE,
        sizeAttenuation: false,
        depthTest: false,
        transparent: true,
        vertexColors: true,
    });

    // Per-node size via a per-vertex `aSize` attribute (the built-in `size` is a uniform),
    // and per-node opacity via a per-vertex `alpha` attribute (marker-opacity).
    material.onBeforeCompile = (shader) => {
        shader.vertexShader =
            "attribute float aSize;\nattribute float alpha;\nvarying float vAlpha;\n" +
            shader.vertexShader
                .replace("gl_PointSize = size;", "gl_PointSize = aSize;")
                .replace("#include <begin_vertex>", "#include <begin_vertex>\n    vAlpha = alpha;");

        patchPointFragmentShader(shader);
        shader.fragmentShader =
            "varying float vAlpha;\n" +
            shader.fragmentShader.replace("diffuseColor.a *= alpha;", "diffuseColor.a *= alpha * vAlpha;");
    };

    sharedPointMaterial = material;
    return material;
}

let sharedFillMaterial: THREE.MeshBasicMaterial | null = null;
function getSharedFillMaterial(): THREE.MeshBasicMaterial {
    if (sharedFillMaterial) return sharedFillMaterial;

    const material = new THREE.MeshBasicMaterial({
        vertexColors: true,
        side: THREE.DoubleSide,
        transparent: true,
        depthTest: false,
    });

    // Per-feature fill-opacity via a per-vertex `alpha` attribute, and a diagonal
    // stripe pattern for crop fills via a per-vertex `stripe` flag (0 or 1).
    material.onBeforeCompile = (shader) => {
        shader.vertexShader =
            "attribute float alpha;\nattribute float stripe;\nvarying float vAlpha;\nvarying float vStripe;\nvarying vec2 vWorld;\n" +
            shader.vertexShader.replace(
                "#include <begin_vertex>",
                "#include <begin_vertex>\n    vAlpha = alpha;\n    vStripe = stripe;\n    vWorld = (modelMatrix * vec4(position, 1.0)).xy;",
            );
        shader.fragmentShader =
            "varying float vAlpha;\nvarying float vStripe;\nvarying vec2 vWorld;\n" +
            shader.fragmentShader.replace(
                "#include <alphatest_fragment>",
                `${stripeCoverageGLSL()}    diffuseColor.a *= vAlpha * mix(1.0, _coverage, vStripe);\n    #include <alphatest_fragment>`,
            );
    };

    sharedFillMaterial = material;
    return material;
}

export class Annotations {
    public state = proxy({
        features: {} as Record<string, Feature>,
        revision: 0, // Bumped every update to enable React re-renders
        input: "idle" as InputState,
        draftType: null as DraftType | null,
        dragNodeIndex: null as number | null,
        dragFeatureId: null as string | null,
        hoverId: null as string | null,
    });

    private readonly journal: Journal;
    private readonly app: App;
    private readonly context: CrossSection | Viewer;
    private readonly prefs: Prefs;
    private readonly lod: LOD;
    private readonly terrain?: Terrain;
    private readonly project: Project;

    private dragPlane = new THREE.Plane();
    private dragPlaneIntersection = new THREE.Vector3();
    private raycaster = new THREE.Raycaster();
    private promoted = new Map<string, FeatureParts>();
    private previewTimeouts = new Map<string, number>();
    private labelSizes = new Map<string, { width: number; height: number }>();
    private chunks = new Map<string, Chunk>();
    private collectionFeatures = new Map<string, Feature>();

    private cellIds = new Map<string, Set<string>>();
    private featureCell = new Map<string, string>();
    private dirtyCells = new Set<string>();

    private loadedTiles = new Set<string>();
    private tileInFlight = 0;
    private generation = 0;

    public group = new THREE.Group();
    public labels = new THREE.Group();
    public outlines = new THREE.Group();

    private get selectionId(): string | null {
        if (this.context instanceof CrossSection) {
            return this.context.state.selectionId;
        }

        return this.app.state.selectionId;
    }

    private set selectionId(value: string | null) {
        if (this.context instanceof CrossSection) {
            this.context.state.selectionId = value;
        } else {
            this.app.state.selectionId = value;
        }
    }

    constructor(
        context: CrossSection | Viewer,
        prefs: Prefs,
        journal: Journal,
        app: App,
        lod: LOD,
        project: Project,
        terrain?: Terrain,
    ) {
        this.context = context;
        this.prefs = prefs;
        this.journal = journal;
        this.app = app;
        this.lod = lod;
        this.project = project;
        this.terrain = terrain;

        context.overlay.add(this.group);
        context.labels.add(this.labels);
        this.group.add(this.outlines);
        this.raycaster.params.Line2 = { threshold: POINT_SIZE };
        this.raycaster.params.Line.threshold = DEFAULT_THRESHOLD;
        this.raycaster.params.Points.threshold = DEFAULT_THRESHOLD;
    }

    private disposeObject(object: THREE.Object3D) {
        if (object instanceof THREE.Mesh || object instanceof THREE.Line || object instanceof THREE.Points) {
            if (object.geometry instanceof THREE.BufferGeometry) {
                object.geometry.dispose();
            }
        }
    }

    private removeObjects(featureId: string) {
        const objects = this.promoted.get(featureId);
        if (!objects) return;

        for (const label of objects.labels) {
            this.labels.remove(label);
        }

        for (const object of Object.values(objects)) {
            if (object instanceof THREE.Object3D) {
                this.group.remove(object);
                this.disposeObject(object);
            }
        }

        this.promoted.delete(featureId);
    }

    private hideObjects(featureId: string) {
        const objects = this.promoted.get(featureId);
        if (objects) {
            objects.points.visible = false;
            objects.draftPoints.visible = false;
            objects.line.visible = false;
            objects.fill.visible = false;
            objects.auxLine.visible = false;
            this.clearLabels(featureId);
        }
    }

    public dispose() {
        for (const id of [...this.promoted.keys()]) {
            this.removeObjects(id);
        }

        for (const object of this.outlines.children) {
            this.disposeObject(object);
        }

        for (const key of this.chunks.keys()) {
            this.disposeChunk(key);
        }

        for (const timeout of this.previewTimeouts.values()) {
            clearTimeout(timeout);
        }

        this.previewTimeouts.clear();
        this.vertices.clear();
        this.outlines.clear();
        this.promoted.clear();
        this.labels.clear();
        this.cellIds.clear();
        this.featureCell.clear();
        this.dirtyCells.clear();
        this.collectionFeatures.clear();
        this.loadedTiles.clear();
    }

    private rebuildGeometry() {
        if (this.dirtyCells.size === 0) return;

        for (const key of this.dirtyCells) {
            const ids = this.cellIds.get(key);
            if (!ids || ids.size === 0) {
                this.disposeChunk(key);
            } else {
                this.buildChunk(key, [...ids]);
            }
        }

        this.dirtyCells.clear();
    }

    public isEmpty(): boolean {
        const projectId = this.project.get().project_id;
        const hasCollections = Object.values(this.app.state.datasets).some(
            (dataset) => dataset.project_id === projectId && dataset.kind === "vector",
        );
        return isEmptyObject(this.state.features) && !hasCollections;
    }

    public isLoading(): boolean {
        return this.tileInFlight > 0;
    }

    public hasFeatures(): boolean {
        const isVisible = (feature: Feature): boolean => {
            if (feature.collectionId) {
                const collection = this.app.state.collections[feature.collectionId];
                if (collection.hidden) return false;
            }

            return feature.properties.hidden ? false : true;
        };

        for (const feature of Object.values(this.state.features)) {
            if (isVisible(feature)) return true;
        }

        for (const feature of this.collectionFeatures.values()) {
            if (isVisible(feature)) return true;
        }

        return false;
    }

    public getFeature(id: string | null): Feature | undefined {
        if (id === null) return undefined;
        return this.state.features[id] ?? this.collectionFeatures.get(id);
    }

    private getFeatureIds(collectionId?: string): string[] {
        const collectionFeatures = Array.from(this.collectionFeatures.keys());

        if (collectionId) {
            return collectionFeatures.filter((id) => {
                return this.getFeature(id)?.collectionId === collectionId;
            });
        }

        return [...Object.keys(this.state.features), ...collectionFeatures];
    }

    public async updateCollection(
        collectionId: string,
        patch: {
            name?: string;
            hidden?: boolean;
            properties?: GeoJSON.GeoJsonProperties;
        },
    ) {
        const collection = this.app.state.collections[collectionId];
        if (!collection) return;

        const { name, hidden, properties } = patch;

        if (name !== undefined) {
            collection.name = name;
            this.app.setDisplayName(collectionId, name);
        }

        if (hidden !== undefined) {
            collection.hidden = hidden;
        }

        if (properties) {
            if (collection.properties) {
                collection.properties = { ...collection.properties, ...properties };
            } else {
                collection.properties = properties;
            }

            // Briefly reveal the members' applied styles when a selected collection changes.
            const changedStyle = Object.keys(properties).some((key) => STYLE_KEYS.has(key));
            if (changedStyle && collectionId === this.selectionId) {
                this.startStylePreview(collectionId);
            }
        }

        this.update([collectionId]);

        if (name !== undefined || properties !== undefined) {
            const projectId = this.project.get().project_id;
            return this.project.updateCollection(projectId, collectionId, {
                name,
                properties: properties !== undefined ? collection.properties : undefined,
            });
        }
    }

    private getFeatureCell(id: string): string | null {
        if (this.promoted.has(id)) return null;

        const feature = this.getFeature(id);
        if (!feature || !feature.geometry) return null;
        if (feature.properties?.hidden) return null;

        if (feature.collectionId && this.app.state.collections[feature.collectionId]?.hidden) return null;

        const vertices = this.getVertices(id);
        if (vertices.length === 0) return null;

        return this.cellKey(vertices[0], vertices[1]);
    }

    // Reconcile a single feature's chunk membership, marking the affected cell(s) dirty
    private syncFeatureCell(id: string) {
        const newCell = this.getFeatureCell(id);
        const oldCell = this.featureCell.get(id);

        if (oldCell === newCell) {
            if (newCell !== null) {
                this.dirtyCells.add(newCell);
            }
            return;
        }

        if (oldCell !== undefined) {
            const bucket = this.cellIds.get(oldCell);
            if (bucket) {
                bucket.delete(id);
                if (bucket.size === 0) {
                    this.cellIds.delete(oldCell);
                }
            }

            this.featureCell.delete(id);
            this.dirtyCells.add(oldCell);
        }

        if (newCell !== null) {
            let bucket = this.cellIds.get(newCell);
            if (!bucket) {
                bucket = new Set();
                this.cellIds.set(newCell, bucket);
            }
            bucket.add(id);

            this.featureCell.set(id, newCell);
            this.dirtyCells.add(newCell);
        }
    }

    public resync() {
        for (const key of [...this.chunks.keys()]) {
            this.disposeChunk(key);
        }

        this.cellIds.clear();
        this.featureCell.clear();
        this.dirtyCells.clear();
        this.vertices.clear();

        const featureIds = this.getFeatureIds();
        for (const id of featureIds) {
            this.syncFeatureCell(id);
        }

        this.update(featureIds);
    }

    private cellKey(x: number, y: number): string {
        return `${Math.floor(x / CHUNK_SIZE)}-${Math.floor(y / CHUNK_SIZE)}`;
    }

    private disposeChunk(key: string) {
        const chunk = this.chunks.get(key);
        if (!chunk) return;
        for (const obj of [chunk.nodes, chunk.lines, chunk.fills]) {
            this.group.remove(obj);
            obj.geometry.dispose();
        }
        this.chunks.delete(key);
    }

    private getOrCreateChunk(key: string): Chunk {
        const existing = this.chunks.get(key);
        if (existing) return existing;

        const nodes = new THREE.Points();
        nodes.layers.set(LAYERS.annotations);
        nodes.material = getSharedPointMaterial();
        nodes.renderOrder = 2;

        const lines = new THREE.LineSegments();
        lines.layers.set(LAYERS.annotations);
        lines.material = getSharedLineMaterial();
        lines.renderOrder = 1;

        const fills = new THREE.Mesh();
        fills.layers.set(LAYERS.annotations);
        fills.material = getSharedFillMaterial();
        fills.renderOrder = 0;

        this.group.add(nodes, lines, fills);
        const chunk: Chunk = { nodes, lines, fills, box: new THREE.Box3() };
        this.chunks.set(key, chunk);
        return chunk;
    }

    private nodePositions: number[] = [];
    private nodeColors: number[] = [];
    private nodeSizes: number[] = [];
    private nodeAlphas: number[] = [];
    private linePositions: number[] = [];
    private lineColors: number[] = [];
    private lineAlphas: number[] = [];
    private fillPositions: number[] = [];
    private fillColors: number[] = [];
    private fillAlphas: number[] = [];
    private fillStripes: number[] = [];
    private fillIndices: number[] = [];
    private chunkColor = new THREE.Color();
    private buildChunk(key: string, ids: string[]) {
        const [cellX, cellY] = key.split("-").map(Number);
        const originX = (cellX + 0.5) * CHUNK_SIZE;
        const originY = (cellY + 0.5) * CHUNK_SIZE;

        this.nodePositions.length = 0;
        this.nodeColors.length = 0;
        this.nodeSizes.length = 0;
        this.nodeAlphas.length = 0;
        this.linePositions.length = 0;
        this.lineColors.length = 0;
        this.lineAlphas.length = 0;
        this.fillPositions.length = 0;
        this.fillColors.length = 0;
        this.fillAlphas.length = 0;
        this.fillStripes.length = 0;
        this.fillIndices.length = 0;
        this.chunkColor.set(0, 0, 0);

        const nodeIndices: number[] = [];
        const nodeFeatureIds: string[] = [];
        const lineFeatureIds: string[] = [];
        const fillFeatureIds: string[] = [];

        for (const id of ids) {
            const feature = this.getFeature(id);
            if (!feature || !feature.geometry) continue;

            const vertices = this.getVertices(id);
            const vertexCount = vertices.length / 3;
            if (vertexCount === 0) continue;

            // Nodes
            const markerSize = this.getMarkerSize(feature);
            const markerOpacity = this.getMarkerOpacity(feature);

            const renderNodes = isPointFeature(feature) || markerOpacity > 0;
            for (let i = 0; renderNodes && i < vertexCount; i++) {
                const x = vertices[i * 3] - originX;
                const y = vertices[i * 3 + 1] - originY;
                const z = vertices[i * 3 + 2];

                this.nodePositions.push(x, y, z);
                this.chunkColor.set(this.getNodeColor(id, i));
                this.nodeColors.push(this.chunkColor.r, this.chunkColor.g, this.chunkColor.b);
                this.nodeSizes.push(markerSize);
                this.nodeAlphas.push(markerOpacity);
                nodeFeatureIds.push(id);
                nodeIndices.push(i);
            }

            // Lines (LineString outline / Polygon ring)
            if (!isPointFeature(feature)) {
                this.chunkColor.set(this.getLineColor(id, feature));
                const strokeOpacity = this.getStrokeOpacity(feature);

                const segments: number[] = [];
                for (let i = 0; i < vertexCount - 1; i++) {
                    segments.push(i, i + 1);
                }

                // Close the polygon ring back to its first vertex (stored open).
                if (isPolygonFeature(feature) && vertexCount >= 3) {
                    segments.push(vertexCount - 1, 0);
                }

                for (const i of segments) {
                    const x = vertices[i * 3] - originX;
                    const y = vertices[i * 3 + 1] - originY;
                    const z = vertices[i * 3 + 2];

                    this.linePositions.push(x, y, z);
                    this.lineColors.push(this.chunkColor.r, this.chunkColor.g, this.chunkColor.b);
                    this.lineAlphas.push(strokeOpacity);
                }

                for (let s = 0; s < segments.length / 2; s++) {
                    lineFeatureIds.push(id);
                }
            }

            // Fills (Polygon). Crops are filled with a diagonal stripe pattern.
            if (isPolygonFeature(feature) && vertexCount >= 3) {
                const triangles = triangulate(vertices);
                if (triangles.length > 0) {
                    const fillColor = this.featureStyle<string>(feature, "fill", this.getLineColor(id, feature));
                    const fillOpacity = this.featureStyle<number>(feature, "fill-opacity", FILL_OPACITY);
                    const stripe = feature.properties.crop ? 1 : 0;
                    this.chunkColor.set(fillColor);

                    const base = this.fillPositions.length / 3;
                    for (let i = 0; i < vertexCount; i++) {
                        const x = vertices[i * 3] - originX;
                        const y = vertices[i * 3 + 1] - originY;
                        const z = vertices[i * 3 + 2];

                        this.fillPositions.push(x, y, z);
                        this.fillColors.push(this.chunkColor.r, this.chunkColor.g, this.chunkColor.b);
                        this.fillAlphas.push(fillOpacity);
                        this.fillStripes.push(stripe);
                    }
                    for (let i = 0; i < triangles.length; i += 3) {
                        this.fillIndices.push(base + triangles[i], base + triangles[i + 1], base + triangles[i + 2]);
                        fillFeatureIds.push(id);
                    }
                }
            }
        }

        const chunk = this.getOrCreateChunk(key);
        for (const obj of [chunk.nodes, chunk.lines, chunk.fills]) {
            obj.position.set(originX, originY, 0);
            obj.updateMatrixWorld();
        }

        chunk.nodes.geometry.dispose();
        const nodeGeom = new THREE.BufferGeometry();
        if (this.nodePositions.length > 0) {
            nodeGeom.setAttribute("position", new THREE.Float32BufferAttribute(this.nodePositions, 3));
            nodeGeom.setAttribute("color", new THREE.Float32BufferAttribute(this.nodeColors, 3));
            nodeGeom.setAttribute("aSize", new THREE.Float32BufferAttribute(this.nodeSizes, 1));
            nodeGeom.setAttribute("alpha", new THREE.Float32BufferAttribute(this.nodeAlphas, 1));
            nodeGeom.computeBoundingSphere();
        }
        chunk.nodes.geometry = nodeGeom;
        chunk.nodes.visible = this.nodePositions.length > 0;
        chunk.nodes.userData = { kind: "nodes", featureIds: nodeFeatureIds, indices: nodeIndices };

        chunk.lines.geometry.dispose();
        const lineGeom = new THREE.BufferGeometry();
        if (this.linePositions.length > 0) {
            lineGeom.setAttribute("position", new THREE.Float32BufferAttribute(this.linePositions, 3));
            lineGeom.setAttribute("color", new THREE.Float32BufferAttribute(this.lineColors, 3));
            lineGeom.setAttribute("alpha", new THREE.Float32BufferAttribute(this.lineAlphas, 1));
            lineGeom.computeBoundingSphere();
        }
        chunk.lines.geometry = lineGeom;
        chunk.lines.visible = this.linePositions.length > 0;
        chunk.lines.userData = { kind: "lines", featureIds: lineFeatureIds };

        chunk.fills.geometry.dispose();
        const fillGeom = new THREE.BufferGeometry();
        if (this.fillIndices.length > 0) {
            fillGeom.setAttribute("position", new THREE.Float32BufferAttribute(this.fillPositions, 3));
            fillGeom.setAttribute("color", new THREE.Float32BufferAttribute(this.fillColors, 3));
            fillGeom.setAttribute("alpha", new THREE.Float32BufferAttribute(this.fillAlphas, 1));
            fillGeom.setAttribute("stripe", new THREE.Float32BufferAttribute(this.fillStripes, 1));
            fillGeom.setIndex(this.fillIndices);
            fillGeom.computeBoundingSphere();
        }
        chunk.fills.geometry = fillGeom;
        chunk.fills.visible = this.fillIndices.length > 0;
        chunk.fills.userData = { kind: "fills", featureIds: fillFeatureIds };

        // Cache bounds for raycasting
        chunk.box.makeEmpty();
        for (const obj of [chunk.nodes, chunk.lines, chunk.fills]) {
            if (obj.visible) chunk.box.expandByObject(obj);
        }

        // Gemoetry starts at full draw range which might not be correct
        this.decimateChunk(chunk);
    }

    // Limit the draw range as the chunk becomes smaller
    private decimateSize = new THREE.Vector3();
    private decimateChunk(chunk: Chunk) {
        const size = chunk.box.getSize(this.decimateSize);
        const worldExtent = Math.max(size.x, size.y);
        const worldHeight = this.getWorldHeight(chunk.box);
        const unitsPerPx = worldHeight / this.context.size.height;
        const screenPx = worldExtent / unitsPerPx;
        const capacity = Math.max(1, (screenPx / POINT_SIZE) ** 2);

        // Every feature (points, lines, polygons) contributes its vertices as
        // nodes, so node count is our proxy for how many features are packed in.
        const nodeCount = chunk.nodes.geometry.getAttribute("position")?.count ?? 0;
        const fraction = nodeCount > 0 ? THREE.MathUtils.clamp(capacity / nodeCount, 0, 1) : 1;
        chunk.nodes.geometry.setDrawRange(0, Math.ceil(nodeCount * fraction));

        const lineVertexCount = chunk.lines.geometry.getAttribute("position")?.count ?? 0;
        const segments = Math.floor(lineVertexCount / 2);
        chunk.lines.geometry.setDrawRange(0, Math.ceil(segments * fraction) * 2);

        const fillIndexCount = chunk.fills.geometry.getIndex()?.count ?? 0;
        const triangles = Math.floor(fillIndexCount / 3);
        chunk.fills.geometry.setDrawRange(0, Math.ceil(triangles * fraction) * 3);
    }

    private decimateChunks() {
        for (const chunk of this.chunks.values()) {
            this.decimateChunk(chunk);
        }
    }

    private throttledDecimate = throttle(() => {
        this.decimateChunks();
        this.context.update();
    }, 100);

    private static readonly TILE_MAX_ZOOM = 20;
    private static readonly TILE_MAX_CONCURRENT = 6;

    private worldBox = new THREE.Box3();
    private worldPoint = new THREE.Vector3();
    private getWorldBox(bbox: [number, number, number, number]): THREE.Box3 {
        const [minLon, minLat, maxLon, maxLat] = bbox;
        const zRange = this.app.state.zRange;
        const minZ = Number.isFinite(zRange[0]) ? zRange[0] : -10_000;
        const maxZ = Number.isFinite(zRange[1]) ? zRange[1] : 10_000;

        this.worldBox.makeEmpty();
        for (const lon of [minLon, maxLon]) {
            for (const lat of [minLat, maxLat]) {
                for (const z of [minZ, maxZ]) {
                    // `zRange` is already scene units, so only the horizontal crosses.
                    const p = this.wgs84ToLocal({ x: lon, y: lat, z: 0 });

                    this.worldPoint.set(p.x, p.y, z);
                    this.worldBox.expandByPoint(this.worldPoint);
                }
            }
        }

        return this.worldBox;
    }

    private tileLonLatBox(z: number, x: number, y: number): [number, number, number, number] {
        const extent = getTileExtent(z, x, y);
        const nw = mercatorToLonLat(extent.minX, extent.maxY);
        const se = mercatorToLonLat(extent.maxX, extent.minY);
        return [nw.lon, se.lat, se.lon, nw.lat];
    }

    private frustum = new THREE.Frustum();
    private viewProj = new THREE.Matrix4();

    public eval = throttle(() => {
        if (!(this.context instanceof Viewer)) return;

        const camera = this.context.camera;
        camera.updateMatrixWorld();
        this.viewProj.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
        this.frustum.setFromProjectionMatrix(this.viewProj);

        const projectId = this.project.get().project_id;
        const wanted: { collectionId: string; z: number; x: number; y: number; dist: number }[] = [];
        for (const [collectionId, dataset] of Object.entries(this.project.sceneDatasets(projectId))) {
            if (dataset.kind !== "vector" || !this.app.state.visible.includes(collectionId)) continue;

            // Load the collection if it doesn't exist
            const collection = this.app.state.collections[collectionId];
            if (!collection) {
                this.app
                    .loadDatasetMeta(collectionId)
                    .then(() => {
                        if (this.project.get().project_id === projectId) {
                            this.eval();
                        }
                    })
                    .catch((err) => {
                        console.error(`Failed to load vector metadata for ${collectionId}`, err);
                    });

                continue;
            }

            if (!collection.hidden && collection.bounds_wgs84) {
                this.walkTiles(collectionId, collection.bounds_wgs84, 0, 0, 0, camera, wanted);
            }
        }

        wanted.sort((a, b) => a.dist - b.dist);
        for (const tile of wanted) {
            if (this.tileInFlight >= Annotations.TILE_MAX_CONCURRENT) break;
            this.loadTile(projectId, tile.collectionId, tile.z, tile.x, tile.y);
        }
    }, 100);

    private tileSize = new THREE.Vector3();
    private walkTiles(
        collectionId: string,
        collectionBbox: [number, number, number, number],
        z: number,
        x: number,
        y: number,
        camera: THREE.Camera,
        out: { collectionId: string; z: number; x: number; y: number; dist: number }[],
    ) {
        const tileBbox = this.tileLonLatBox(z, x, y);
        if (!bboxesIntersect(tileBbox, collectionBbox)) return;

        const box = this.getWorldBox(tileBbox);
        if (!this.frustum.intersectsBox(box)) return;

        const size = box.getSize(this.tileSize);
        const span = Math.max(size.x, size.y);
        const dist = Math.max(1, box.distanceToPoint(camera.position));

        const shouldSplit = z < Annotations.TILE_MAX_ZOOM && dist < span;
        if (shouldSplit) {
            for (let oy = 0; oy < 2; oy++) {
                for (let ox = 0; ox < 2; ox++) {
                    this.walkTiles(collectionId, collectionBbox, z + 1, x * 2 + ox, y * 2 + oy, camera, out);
                }
            }
            return;
        }

        const key = `${collectionId}/${tileKey(z, x, y)}`;
        if (this.loadedTiles.has(key)) return;
        out.push({ collectionId, z, x, y, dist });
    }

    private loadTile(projectId: string, collectionId: string, z: number, x: number, y: number) {
        const key = `${collectionId}/${tileKey(z, x, y)}`;
        this.loadedTiles.add(key); // Mark before fetch so it isn't re-queued; cleared on failure
        this.tileInFlight++;
        const generation = this.generation;

        this.project
            .getFeatureTile(projectId, collectionId, z, x, y)
            .then((features) => {
                if (generation == this.generation) {
                    this.addFeatures(features);
                }
            })
            .catch((err) => {
                if (generation === this.generation) {
                    this.loadedTiles.delete(key);
                    console.error(`Failed to load feature tile ${collectionId} ${z}/${x}/${y}`, err);
                }
            })
            .finally(() => {
                if (generation === this.generation) {
                    this.tileInFlight--;
                    this.eval(); // Load tiles that were over the concurrency cap
                }
            });
    }

    private promote(featureId: string) {
        if (!this.promoted.has(featureId)) {
            this.invalidate(featureId);
            const points = new THREE.Points();
            points.layers.set(LAYERS.annotations);
            points.userData.id = featureId;
            points.userData.isNode = true;
            this.group.add(points);

            // The draft node lives on the draft layer so the raycaster never hits it.
            const draftPoints = new THREE.Points();
            draftPoints.layers.set(LAYERS.draft);
            draftPoints.userData.id = featureId;
            draftPoints.userData.isNode = true;
            draftPoints.visible = false;
            this.group.add(draftPoints);

            // line/fill start hidden and are shown once updateLine/updateFill set their
            // geometry, so the raycaster never sees an empty Line2/Mesh.
            const line = new Line2();
            line.layers.set(LAYERS.annotations);
            line.userData.id = featureId;
            line.userData.kind = "lines";
            line.visible = false;
            this.group.add(line);

            const fill = new THREE.Mesh();
            fill.layers.set(LAYERS.annotations);
            fill.userData.id = featureId;
            fill.visible = false;
            this.group.add(fill);

            // A single dashed line, reused for either the trailing draft segment or the
            // height/ground decomposition of a 2-node line (never both at once).
            const auxLine = new Line2();
            auxLine.layers.set(LAYERS.annotations);
            auxLine.userData.id = featureId;
            auxLine.visible = false;
            this.group.add(auxLine);

            this.promoted.set(featureId, {
                points,
                draftPoints,
                line,
                fill,
                auxLine,
                labels: [],
            });
        }

        this.syncFeatureCell(featureId);
    }

    private demote(featureId: string) {
        if (this.promoted.has(featureId)) {
            this.invalidate(featureId);
            this.removeObjects(featureId);
        }

        this.syncFeatureCell(featureId);
    }

    public reset() {
        // Invalidate any in-flight tile loads so their completions become no-ops instead of
        // re-adding features or re-enqueuing work against the reset state.
        this.generation++;
        this.tileInFlight = 0;
        setRecord(this.state.features, {});
        this.loadedTiles.clear();
        this.state.input = "idle";
        this.state.draftType = null;
        this.state.dragNodeIndex = null;
        this.state.dragFeatureId = null;
        this.state.hoverId = null;
        this.dispose();
    }

    public updateFeature(
        featureId: string,
        patch: { geometry?: Feature["geometry"]; properties?: Feature["properties"] },
    ) {
        const projectId = this.project.get().project_id;

        const feature = this.getFeature(featureId);
        if (!feature) return;

        if (patch.properties) {
            const merged = { ...feature.properties, ...patch.properties };
            for (const [key, value] of Object.entries(merged)) {
                if (value === undefined) {
                    delete merged[key as keyof typeof merged];
                }
            }

            feature.properties = merged;

            if (this.isFeatureSelected(featureId)) {
                if (Object.keys(patch.properties).some((key) => STYLE_KEYS.has(key))) {
                    this.startStylePreview(featureId);
                }
            }
        }

        if (patch.geometry) {
            feature.geometry = patch.geometry;
            this.invalidate(featureId);
        }

        this.project.upsertFeature(projectId, featureId, feature);
        this.update([featureId]);
    }

    public deleteFeature(featureId: string) {
        this.journal.write();

        if (featureId === this.selectionId) {
            this.selectionId = null;
        }

        delete this.state.features[featureId];
        this.collectionFeatures.delete(featureId);
        const { project_id } = this.project.get();
        this.project.deleteFeature(project_id, featureId);

        this.demote(featureId);
        this.rebuildGeometry();
        this.context.update();
    }

    public deleteCollection(collectionId: string) {
        this.journal.write();

        if (collectionId === this.selectionId) {
            this.selectionId = null;
        }

        for (const id of this.getFeatureIds(collectionId)) {
            this.collectionFeatures.delete(id);
            this.demote(id);
        }
        delete this.app.state.collections[collectionId];
        delete this.app.state.datasets[collectionId];

        for (const key of this.loadedTiles.keys()) {
            if (key.startsWith(`${collectionId}/`)) {
                this.loadedTiles.delete(key);
            }
        }

        const { project_id } = this.project.get();
        this.project.deleteCollection(project_id, collectionId);

        this.rebuildGeometry();
        this.context.update();
    }

    public cancelDraft() {
        if (this.state.input !== "drafting") return;

        const featureId = this.selectionId;
        this.state.input = "idle";
        this.state.draftType = null;

        if (!featureId) return;

        delete this.state.features[featureId];
        this.demote(featureId);
        this.selectionId = null;
        this.update([featureId]);
    }

    public startFeature(type: DraftType) {
        this.journal.batch();

        const featureId = THREE.MathUtils.generateUUID();
        const selection = this.selectionId;

        if (selection && this.state.input === "drafting") {
            delete this.state.features[selection];
            this.demote(selection);
            this.removeObjects(selection);
        }

        let feature: Feature;
        switch (type) {
            case "point":
                feature = {
                    type: "Feature",
                    geometry: { type: "Point", coordinates: [] },
                    properties: { "dataset-id": null },
                };
                break;
            case "line":
            case "path":
                feature = {
                    type: "Feature",
                    geometry: { type: "LineString", coordinates: [] },
                    properties: {},
                };
                break;
            case "area":
                feature = {
                    type: "Feature",
                    geometry: { type: "Polygon", coordinates: [[]] },
                    properties: {},
                };
                break;
        }

        feature.id = featureId;
        this.state.features[featureId] = feature;
        this.state.input = "drafting";
        this.state.draftType = type;
        this.selectionId = featureId;

        this.promote(featureId);
        this.update([featureId]);

        // Focus on the next frame to avoid immediate focus loss when DOM elemnts change
        requestAnimationFrame(() => {
            this.context.renderer?.domElement.focus();
        });

        return featureId;
    }

    public completeFeature(featureId: string) {
        this.state.input = "idle";
        this.state.draftType = null;
        const { project_id } = this.project.get();
        const feature = this.getFeature(featureId);
        if (!feature) return;
        this.project.upsertFeature(project_id, featureId, feature);
        this.update([featureId]);
    }

    public setOutlines(outlines: Outline[]) {
        this.outlines.clear();

        for (const outline of outlines) {
            const resolution = this.context.size;
            const line2Material = getLine2Material(outline.color, 6, resolution);

            for (const polygon of outline.boundary.coordinates) {
                // Render all rings: exterior (index 0) and holes (index 1+)
                for (const ring of polygon) {
                    if (ring.length === 0) continue;

                    // Anchor at the first vertex in absolute scene coords
                    const [ax, ay] = this.app.fromWGS84(ring[0][0], ring[0][1]);
                    const positions: number[] = [];

                    for (const coord of ring) {
                        const [x, y] = this.app.fromWGS84(coord[0], coord[1]);

                        positions.push(x - ax, y - ay, 0);
                    }

                    const lineGeometry = new LineGeometry();
                    lineGeometry.setPositions(positions);

                    const line = new Line2(lineGeometry, line2Material);
                    const ox = ax - this.app.state.offset.x;
                    const oy = ay - this.app.state.offset.y;
                    const oz = outline.z - this.app.state.offset.z;

                    line.position.set(ox, oy, oz);
                    line.layers.set(LAYERS.annotations);
                    this.outlines.add(line);
                }
            }
        }
        this.context.update();
    }

    private featurePriority(featureId: string): number {
        const feature = this.getFeature(featureId);

        if (!feature || !feature.geometry) {
            return 4;
        }

        switch (feature.geometry.type) {
            case "Point":
                return 0;
            case "LineString":
                return feature.geometry.coordinates.length <= 2 ? 1 : 2;
            case "Polygon":
                return 3;
            default:
                return 4;
        }
    }

    // Resolve raycast intersection into an Object3DHit + featureId
    private resolveIntersect(intersect: THREE.Intersection): { hit: Object3DHit; featureId: string } | null {
        const object = intersect.object;
        const kind = object.userData.kind;

        if (kind === "nodes") {
            if (intersect.index === undefined) return null;

            const featureId = object.userData.featureIds[intersect.index];
            if (featureId === undefined) return null;

            return {
                featureId,
                hit: {
                    type: "object",
                    userData: { id: featureId, isNode: true },
                    index: object.userData.indices[intersect.index],
                    position: intersect.point,
                },
            };
        }

        // Chunked geometry uses `userData.featureIds` instead of `userData.id`
        const hasFeatureIds = Array.isArray(object.userData.featureIds);
        if (hasFeatureIds) {
            if (kind === "lines") {
                if (intersect.index === undefined) return null;

                const featureIndex = Math.floor(intersect.index / 2);
                const featureId = object.userData.featureIds[featureIndex];
                if (featureId === undefined) return null;

                return {
                    featureId,
                    hit: {
                        type: "object",
                        userData: {
                            id: featureId,
                            kind: "lines",
                        },
                        position: intersect.point,
                    },
                };
            }

            if (kind === "fills") {
                if (intersect.faceIndex == null) return null;

                const featureIndex = intersect.faceIndex;
                const featureId = object.userData.featureIds[featureIndex];
                if (featureId === undefined) return null;

                return {
                    featureId,
                    hit: {
                        type: "object",
                        userData: {
                            id: featureId,
                            kind: "fills",
                        },
                        position: intersect.point,
                    },
                };
            }
        }

        const id = object.userData.id;
        if (typeof id !== "string") return null;

        return {
            featureId: id,
            hit: {
                type: "object",
                userData: object.userData,
                index: intersect.index,
                position: intersect.point,
            },
        };
    }

    private getWorldHeight(box: THREE.Box3) {
        if (this.context.camera instanceof THREE.OrthographicCamera) {
            return this.context.camera.top - this.context.camera.bottom;
        } else {
            const distance = Math.max(1, box.distanceToPoint(this.context.camera.position));
            return 2 * distance * Math.tan(FOV_RAD / 2);
        }
    }

    private intersectionBox = new THREE.Box3();
    private thresholdBox = new THREE.Box3();
    private boxInteresection = new THREE.Vector3();
    private intersectionTargets: THREE.Object3D[] = [];
    private intersectsAnnotation(position: THREE.Vector2): Object3DHit[] {
        this.intersectionTargets.length = 0;
        this.raycaster.setFromCamera(position, this.context.camera);
        this.raycaster.layers.set(LAYERS.annotations);

        let promotedThreshold = Infinity;
        for (const parts of this.promoted.values()) {
            for (const obj of Object.values(parts)) {
                if (obj instanceof THREE.Object3D && obj.visible) {
                    this.intersectionTargets.push(obj);

                    this.thresholdBox.setFromObject(obj);
                    if (!this.thresholdBox.isEmpty()) {
                        const worldHeight = this.getWorldHeight(this.thresholdBox);
                        const unitsPerPixel = worldHeight / this.context.size.height;
                        promotedThreshold = Math.min(promotedThreshold, unitsPerPixel * POINT_SIZE);
                    }
                }
            }
        }

        let chunkThreshold = Infinity;
        for (const chunk of this.chunks.values()) {
            const worldHeight = this.getWorldHeight(chunk.box);
            const unitsPerPixel = worldHeight / this.context.size.height;
            chunkThreshold = Math.min(chunkThreshold, unitsPerPixel * POINT_SIZE);

            this.intersectionBox.copy(chunk.box).expandByScalar(chunkThreshold);

            for (const obj of Object.values(chunk)) {
                if (obj instanceof THREE.Object3D && obj.visible) {
                    if (this.raycaster.ray.intersectBox(this.intersectionBox, this.boxInteresection)) {
                        this.intersectionTargets.push(obj);
                    }
                }
            }
        }

        const threshold = Math.min(chunkThreshold, promotedThreshold);
        if (threshold !== Infinity) {
            this.raycaster.params.Points.threshold = threshold;
            this.raycaster.params.Line.threshold = threshold;
        }

        const intersects = this.raycaster.intersectObjects(this.intersectionTargets);
        const resolved: { hit: Object3DHit; priority: number }[] = [];
        for (const intersect of intersects) {
            const result = this.resolveIntersect(intersect);
            if (result) {
                resolved.push({
                    hit: result.hit,
                    priority: this.featurePriority(result.featureId),
                });
            }
        }

        return resolved.sort((a, b) => a.priority - b.priority).map((r) => r.hit);
    }

    // Features are WGS84 lon/lat with a height in metres, as GeometryZ(4326) means.
    // Scene space is the scene CRS's own unit.
    wgs84ToLocal(wgs84: THREE.Vector3Like) {
        const [absoluteX, absoluteY] = this.app.fromWGS84(wgs84.x, wgs84.y);
        const localX = absoluteX - this.app.state.offset.x;
        const localY = absoluteY - this.app.state.offset.y;
        const localZ = metersToScene(wgs84.z, this.app.state.sceneUnit);

        return { x: localX, y: localY, z: localZ };
    }

    localToWGS84(local: THREE.Vector3Like) {
        const absoluteX = local.x + this.app.state.offset.x;
        const absoluteY = local.y + this.app.state.offset.y;
        const absoluteZ = sceneToMeters(local.z, this.app.state.sceneUnit);
        const [wgs84X, wgs84Y] = this.app.toWGS84(absoluteX, absoluteY);

        return { x: wgs84X, y: wgs84Y, z: absoluteZ };
    }

    private readonly MAX_VERTEX_CACHE_SIZE = 10_000;
    private vertices = new Map<string, number[]>();
    private cacheVertices(key: string, value: number[]) {
        this.vertices.set(key, value);
        if (this.vertices.size > this.MAX_VERTEX_CACHE_SIZE) {
            const oldestKey = this.vertices.keys().next().value;
            if (oldestKey) {
                this.vertices.delete(oldestKey);
            }
        }
    }

    private getVertexKey(featureId: string): string {
        const { x, y, z } = this.app.state.offset;
        return `${featureId}-${this.app.state.proj}-${x}-${y}-${z}`;
    }

    private invalidate(featureId: string) {
        this.vertices.delete(this.getVertexKey(featureId));
    }

    private getVertices(featureId: string): number[] {
        const feature = this.getFeature(featureId);
        if (!feature || !feature.geometry) return [];

        const key = this.getVertexKey(featureId);
        const cached = this.vertices.get(key);
        if (cached) return cached;

        let vertices: number[] = [];
        switch (feature.geometry.type) {
            case "Point": {
                if (feature.geometry.coordinates.length > 0) {
                    vertices = this.toLocalVec(feature.geometry.coordinates);
                }
                break;
            }
            case "LineString": {
                vertices = feature.geometry.coordinates.flatMap((position) => {
                    return this.toLocalVec(position);
                });
                break;
            }
            case "Polygon": {
                vertices = feature.geometry.coordinates[0].flatMap((position) => {
                    return this.toLocalVec(position);
                });
                break;
            }
        }

        if (!this.promoted.has(featureId)) {
            this.cacheVertices(key, vertices);
        }

        return vertices;
    }

    private toLocalVec(pos: GeoJSON.Position): [number, number, number] {
        const { x, y, z } = this.wgs84ToLocal({ x: pos[0], y: pos[1], z: pos[2] ?? 0 });
        return [x, y, z];
    }

    public handleInput(event: InputEvent): boolean {
        switch (this.state.input) {
            case "idle":
                return this.handleIdleInput(event);
            case "drafting":
                return this.handleDraftingInput(event);
            case "dragging":
                return this.handleDraggingInput(event);
        }
    }

    private pickNode(event: ViewerEvent): { featureId: string; index: number; position: GeoJSON.Position } | null {
        const objects = this.intersectsAnnotation(event.position);

        for (const object of objects) {
            if (
                typeof object.userData.id === "string" &&
                object.userData.isNode === true &&
                typeof object.index === "number"
            ) {
                const feature = this.getFeature(object.userData.id);
                if (!feature) continue;

                const positions = featurePositions(feature);
                const position = positions[object.index];

                if (position) {
                    return {
                        featureId: object.userData.id,
                        index: object.index,
                        position: position,
                    };
                }
            }
        }

        return null;
    }

    private canCloseDraft(feature: Feature, positions: GeoJSON.Position[]): boolean {
        const isPolygon = isPolygonFeature(feature);
        const isDrafting = this.state.input === "drafting";
        return isDrafting && isPolygon && positions.length > 3;
    }

    private pickSnap(
        event: ViewerEvent,
        currentFeatureId: string,
    ): { featureId: string; index: number | null; position: GeoJSON.Position; kind: "node" | "line" } | null {
        const objects = this.intersectsAnnotation(event.position);

        let lineHit: {
            featureId: string;
            index: null;
            position: GeoJSON.Position;
            kind: "line";
        } | null = null;

        for (const object of objects) {
            const id = object.userData.id;
            if (typeof id !== "string") continue;

            const feature = this.getFeature(id);
            if (!feature) continue;

            // Snap to the first node of polygons being drafted
            if (object.userData.isNode === true && typeof object.index === "number") {
                const isFirstNode = object.index === 0;
                const isCurrent = id === currentFeatureId;
                const positions = featurePositions(feature);
                const canClose = this.canCloseDraft(feature, positions);

                if (isCurrent && canClose && isFirstNode) {
                    const position = positions[object.index];
                    if (position) {
                        return { featureId: id, index: object.index, position, kind: "node" };
                    }
                }
            } else if (object.userData.kind === "lines" && id !== currentFeatureId && !lineHit) {
                const wgs84 = this.localToWGS84(object.position);
                lineHit = { featureId: id, index: null, position: [wgs84.x, wgs84.y, wgs84.z], kind: "line" };
            }
        }

        return lineHit;
    }

    public selectFeature(featureId: string) {
        if (this.state.input === "drafting" && featureId !== this.selectionId) {
            this.cancelDraft();
        }

        const previous = this.selectionId;
        this.selectionId = featureId;
        this.update(previous ? [previous, featureId] : [featureId]);
    }

    public hoverFeature(featureId: string | null) {
        this.setHover(featureId);

        if (this.context.renderer) {
            if (featureId) {
                this.context.renderer.domElement.style.cursor = "pointer";
            } else {
                this.context.renderer.domElement.style.cursor = "default";
            }
        }
    }

    public setHover(hoverId: string | null) {
        const previous = this.state.hoverId;
        if (previous === hoverId) return;

        this.state.hoverId = hoverId;
        this.update([previous, hoverId].filter((id) => id !== null));
    }

    private isFeatureSelected(featureId: string): boolean {
        if (featureId === this.selectionId) return true;

        const collectionId = this.getFeature(featureId)?.collectionId;
        return collectionId !== undefined && collectionId === this.selectionId;
    }

    private isFeatureHovered(featureId: string): boolean {
        if (featureId === this.state.hoverId) return true;

        const collectionId = this.getFeature(featureId)?.collectionId;
        return collectionId !== undefined && collectionId === this.state.hoverId;
    }

    private isStylePreviewing(featureId: string): boolean {
        if (this.previewTimeouts.has(featureId)) {
            return true;
        }

        const collectionId = this.getFeature(featureId)?.collectionId;
        return collectionId !== undefined && this.previewTimeouts.has(collectionId);
    }

    private startStylePreview(id: string) {
        const existing = this.previewTimeouts.get(id);
        if (existing) clearTimeout(existing);

        const timeout = setTimeout(() => {
            this.previewTimeouts.delete(id);
            this.update([id]);
        }, STYLE_PREVIEW_MS);

        this.previewTimeouts.set(id, timeout);
    }

    private handleIdleInput(event: InputEvent): boolean {
        switch (event.type) {
            case "key-down": {
                const selectionId = this.selectionId;

                if (!selectionId) {
                    return false;
                }

                if (event.originalEvent.key === "Backspace" || event.originalEvent.key === "Delete") {
                    const feature = this.getFeature(selectionId);
                    const collections = this.app.state.collections;
                    if (isFeatureLocked(feature, collections)) {
                        return false;
                    }

                    this.deleteFeature(selectionId);
                    return true;
                } else if (event.originalEvent.key === "Escape") {
                    this.selectionId = null;
                    this.update([selectionId]);
                    return true;
                }

                return false;
            }
            case "pointer-down": {
                const objects = this.intersectsAnnotation(event.position);

                return objects.length > 0;
            }
            case "drag-start": {
                if (event.originalEvent.button !== 0) {
                    return false;
                }

                const node = this.pickNode(event);

                if (node) {
                    const feature = this.getFeature(node.featureId);
                    const collections = this.app.state.collections;
                    if (isFeatureLocked(feature, collections)) {
                        return false;
                    }

                    this.journal.write();
                    this.state.input = "dragging";
                    this.state.dragNodeIndex = node.index;
                    this.state.dragFeatureId = node.featureId;
                    this.promote(node.featureId);

                    return true;
                }

                return false;
            }
            case "double-click": {
                const objects = this.intersectsAnnotation(event.position);

                return objects.length > 0;
            }
            case "move": {
                if (this.context.renderer) {
                    const objects = this.intersectsAnnotation(event.position);
                    const userData = objects[0]?.userData;
                    const featureId = typeof userData?.id === "string" ? userData.id : null;
                    this.hoverFeature(featureId);
                }
                break;
            }
            case "click": {
                const hits = this.intersectsAnnotation(event.position);
                if (hits.length > 0) {
                    const featureId = hits[0]?.userData.id;
                    if (typeof featureId === "string") {
                        this.selectFeature(featureId);
                    }
                } else if (this.selectionId) {
                    this.update([this.selectionId]);
                    this.selectionId = null;
                }

                break;
            }
            case "zoom":
            case "pinch": {
                this.throttledDecimate();
                break;
            }
            case "zoom-end":
            case "drag-end": {
                const ids = [this.selectionId, this.state.dragFeatureId];
                this.update(ids.filter((id): id is string => id !== null));
            }
        }
        return false;
    }

    private intersects(event: ViewerEvent): PointHit | null {
        if (this.context instanceof Viewer) {
            const pointHit = this.lod.pickPoint(event.position);
            if (pointHit) return pointHit;

            const mapHit = this.terrain?.getHeightAtScreenPosition(event.position);
            if (mapHit) {
                return {
                    type: "point",
                    position: mapHit,
                    tile: null,
                } satisfies PointHit;
            }

            return null;
        } else {
            const ray = this.context.getMouseRay(event.position);
            const intersection = new THREE.Vector3();

            if (ray.intersectPlane(this.context.fallbackPlane, intersection)) {
                return { type: "point", position: intersection, tile: null };
            }

            return null;
        }
    }

    private updateDraftNode(event: ViewerEvent) {
        const featureId = this.selectionId;
        if (!featureId) return false;

        const feature = this.getFeature(featureId);
        if (!feature) return false;

        const geometry = feature.geometry;
        if (!geometry) return false;

        const snap = this.pickSnap(event, featureId);
        const point = this.intersects(event);
        const vertices = this.getVertices(featureId);
        const vertexCount = vertices.length / 3;
        const index = Math.max(0, vertexCount - 1);

        if (snap) {
            setPosition(feature, index, [...snap.position]);

            if (isPointFeature(feature) && snap.kind === "node") {
                const target = this.getFeature(snap.featureId);
                if (target && "dataset-id" in target.properties) {
                    feature.properties["dataset-id"] = target.properties["dataset-id"];
                } else {
                    feature.properties["dataset-id"] = null;
                }
            }
        } else if (point) {
            const datasetId = this.getPointDatasetId(point);
            const wgs84 = this.localToWGS84(point.position);
            setPosition(feature, index, [wgs84.x, wgs84.y, wgs84.z]);

            if (isPointFeature(feature)) {
                feature.properties["dataset-id"] = datasetId;
            }
        }

        this.update([featureId]);
    }

    private getPointDatasetId(point: PointHit) {
        if (!point.tile) {
            return null;
        }

        const tableName = point.tile.tableName;
        const info = this.lod.getTableInfo(tableName);
        return info?.datasetId ?? null;
    }

    private handleDraftingInput(event: InputEvent): boolean {
        const featureId = this.selectionId;

        if (!featureId) {
            return false;
        }

        const feature = this.getFeature(featureId);
        if (!feature) return false;

        const draftType = this.state.draftType;
        const positions = featurePositions(feature);
        const positionCount = positions.length;

        switch (event.type) {
            case "key-down": {
                if (event.originalEvent.key === "Backspace") {
                    if (positionCount < 2) {
                        this.deleteFeature(featureId);
                        this.state.input = "idle";
                        this.state.draftType = null;
                        this.selectionId = null;
                    } else {
                        spliceCoord(feature, positionCount - 2, 1);
                        this.update([featureId]);
                    }
                    return true;
                } else if (event.originalEvent.key === "Delete") {
                    this.deleteFeature(featureId);
                    this.state.input = "idle";
                    this.state.draftType = null;
                    return true;
                } else if (event.originalEvent.key === "Escape") {
                    popPosition(feature);

                    let requiredCount = 0;
                    if (draftType === "point") {
                        requiredCount = 1;
                    } else if (draftType === "line") {
                        requiredCount = 2;
                    } else if (draftType === "path") {
                        requiredCount = 3;
                    } else if (draftType === "area") {
                        requiredCount = 3;
                    }

                    if (positionCount < requiredCount) {
                        this.deleteFeature(featureId);
                    } else {
                        this.completeFeature(featureId);
                    }

                    this.selectionId = null;
                    this.state.input = "idle";
                    this.state.draftType = null;
                    return true;
                }
                return false;
            }
            case "pointer-down": {
                // Draft node is updated in "move" for non-touch devices
                if (navigator.maxTouchPoints > 0) {
                    this.updateDraftNode(event);
                }

                // Always indicate as handled to prevent pivot flashing
                return true;
            }
            case "double-click": {
                if (draftType === "path" && positionCount > 2) {
                    popPosition(feature);
                    this.completeFeature(featureId);

                    return true;
                }

                if (draftType === "area" && positionCount > 3) {
                    popPosition(feature);
                    this.completeFeature(featureId);
                    return true;
                }
                return false;
            }
            case "click": {
                if (event.originalEvent.button !== 0 || positionCount === 0) {
                    return false;
                }

                if (draftType === "path" && positionCount > 2) {
                    const node = this.pickNode(event);

                    if (node?.featureId === featureId && node.index === positionCount - 2) {
                        popPosition(feature);
                        this.completeFeature(featureId);
                        return true;
                    }
                } else if (draftType === "area" && positionCount > 3) {
                    const node = this.pickNode(event);

                    if (node?.featureId === featureId && node.index === 0) {
                        popPosition(feature);
                        this.completeFeature(featureId);
                        return true;
                    }
                }

                if (draftType === "line" && positionCount === 2) {
                    this.completeFeature(featureId);
                    return true;
                } else if (draftType === "point") {
                    this.completeFeature(featureId);
                    return true;
                }

                const lastPosition = featurePositions(feature).at(-1);
                if (lastPosition) {
                    pushPosition(feature, [...lastPosition]);
                }
                this.update([featureId]);

                return true;
            }
            case "move": {
                this.updateDraftNode(event);
                return true;
            }
        }
        return false;
    }

    private handleNodePointDrag(event: DragEvent): boolean {
        if (this.state.dragNodeIndex === null || this.state.dragFeatureId === null) {
            return false;
        }

        const feature = this.getFeature(this.state.dragFeatureId);
        if (!feature) return false;

        const snap = this.pickSnap(event, this.state.dragFeatureId);
        if (snap) {
            setPosition(feature, this.state.dragNodeIndex, [...snap.position]);

            if (isPointFeature(feature) && snap.kind === "node") {
                const target = this.getFeature(snap.featureId);
                if (target && "dataset-id" in target.properties) {
                    feature.properties["dataset-id"] = target.properties["dataset-id"];
                } else {
                    feature.properties["dataset-id"] = null;
                }
            }

            this.update([this.state.dragFeatureId]);
            return true;
        }

        const point = this.intersects(event);
        if (!point) return false;

        const datasetId = this.getPointDatasetId(point);
        const wgs84 = this.localToWGS84(point.position);

        setPosition(feature, this.state.dragNodeIndex, [wgs84.x, wgs84.y, wgs84.z]);

        if (isPointFeature(feature)) {
            feature.properties["dataset-id"] = datasetId;
        }

        this.update([this.state.dragFeatureId]);

        return true;
    }

    private handleNodePlaneDrag(event: DragEvent): boolean {
        if (this.state.dragNodeIndex === null || this.state.dragFeatureId === null) {
            return false;
        }

        const ray = this.context.getMouseRay(event.position);
        const feature = this.getFeature(this.state.dragFeatureId);
        if (!feature) return false;

        const positions = featurePositions(feature);
        const position = positions[this.state.dragNodeIndex];
        if (!position) return false;

        this.dragPlane.set(UNIT_Z, -this.toLocalVec(position)[2]);
        ray.intersectPlane(this.dragPlane, this.dragPlaneIntersection);

        const wgs84 = this.localToWGS84(this.dragPlaneIntersection);
        setPosition(feature, this.state.dragNodeIndex, [wgs84.x, wgs84.y, wgs84.z]);
        this.update([this.state.dragFeatureId]);

        return true;
    }

    private handleDraggingInput(event: InputEvent): boolean {
        const { project_id } = this.project.get();

        switch (event.type) {
            case "drag": {
                if (![0, -1].includes(event.originalEvent.button)) {
                    return false;
                }

                if (event.originalEvent.shiftKey) {
                    return this.handleNodePlaneDrag(event);
                } else {
                    return this.handleNodePointDrag(event);
                }
            }
            case "drag-end": {
                const featureId = this.state.dragFeatureId;

                if (featureId) {
                    this.demote(featureId);
                    const feature = this.getFeature(featureId);

                    if (feature) {
                        this.project.upsertFeature(project_id, featureId, feature);
                        this.update([featureId]);
                    }
                }

                this.state.dragNodeIndex = null;
                this.state.dragFeatureId = null;
                this.state.input = "idle";
                return true;
            }
        }
        return false;
    }

    private updateLine(featureId: string) {
        const feature = this.getFeature(featureId);
        if (!feature || !feature.geometry) return;

        const parts = this.promoted.get(featureId);
        if (!parts) return;

        const opacity = this.getStrokeOpacity(feature);
        const color = this.getLineColor(featureId, feature);
        const width = this.getLineWidth(featureId, feature);
        const vertices = this.getVertices(featureId);
        const anchor = this.getAnchor(vertices);
        const vertexCount = vertices.length / 3;
        const isDrafting = this.state.input === "drafting" && this.selectionId === featureId;

        // While drafting, the solid line covers only the committed nodes; the trailing
        // segment that follows the cursor is rendered dashed on `auxLine`.
        const solidCount = isDrafting ? vertexCount - 1 : vertexCount;
        const solidVertices = vertices.slice(0, solidCount * 3);

        // Close the polygon outline back to its first vertex (the ring is stored open).
        if (isPolygonFeature(feature) && !isDrafting && solidCount >= 3) {
            solidVertices.push(vertices[0], vertices[1], vertices[2]);
        }

        this.subtractAnchor(solidVertices, anchor);

        if (solidVertices.length >= 6) {
            const resolution = this.context.size;
            parts.line.visible = true;
            parts.line.position.copy(anchor);
            parts.line.updateMatrixWorld();
            parts.line.geometry.setPositions(solidVertices);
            // @ts-expect-error - private property
            delete parts.line.geometry._maxInstanceCount;
            parts.line.material = getLine2Material(color, width, resolution, opacity);
            parts.line.geometry.computeBoundingSphere();
            parts.line.computeLineDistances();
        } else {
            parts.line.visible = false;
        }

        if (isDrafting && vertexCount >= 2) {
            const start = new THREE.Vector3().fromArray(vertices, (vertexCount - 2) * 3);
            const end = new THREE.Vector3().fromArray(vertices, (vertexCount - 1) * 3);
            this.setAuxLine(parts, [start, end], anchor, color, width, opacity, this.getDashSize([start, end]));
        } else {
            // The 2-node decomposition (set in updateLabels) may re-enable this.
            parts.auxLine.visible = false;
        }
    }

    private setAuxLine(
        parts: FeatureParts,
        points: THREE.Vector3[],
        anchor: THREE.Vector3,
        color: string,
        lineWidth: number,
        opacity: number,
        dashSize: number,
    ) {
        const resolution = this.context.size;
        const positions: number[] = [];
        for (const point of points) {
            positions.push(point.x - anchor.x, point.y - anchor.y, point.z - anchor.z);
        }

        parts.auxLine.visible = true;
        parts.auxLine.position.copy(anchor);
        parts.auxLine.updateMatrixWorld();
        parts.auxLine.geometry.setPositions(positions);
        // @ts-expect-error - private property
        delete parts.auxLine.geometry._maxInstanceCount;
        parts.auxLine.material = getLine2Material(color, lineWidth, resolution, opacity, dashSize);
        parts.auxLine.geometry.computeBoundingSphere();
        parts.auxLine.computeLineDistances();
    }

    private getDashSize(points: THREE.Vector3[]) {
        if (this.context.camera instanceof THREE.OrthographicCamera) {
            const worldHeight = this.context.camera.top - this.context.camera.bottom;
            const unitsPerPx = worldHeight / this.context.size.height;

            return TARGET_DASH_SIZE * unitsPerPx;
        }

        const splits = [0.125, 0.25, 0.375, 0.5, 0.625, 0.75, 0.875, 1];

        let combinedDistance = 0;
        for (let i = 0; i < points.length - 1; i++) {
            const start = points[i];
            const end = points[i + 1];

            for (const split of splits) {
                const point = new THREE.Vector3().lerpVectors(start, end, split);
                combinedDistance += point.distanceTo(this.context.camera.position);
            }
        }

        const distanceCount = (points.length - 1) * splits.length;
        const averageDistance = combinedDistance / distanceCount;
        const worldHeight = 2 * averageDistance * Math.tan(FOV_RAD / 2);
        const unitsPerPx = worldHeight / this.context.size.height;

        return TARGET_DASH_SIZE * unitsPerPx;
    }

    private isCrossSection(feature: Feature) {
        return "cross-section" in feature.properties && feature.properties["cross-section"] === true;
    }

    private featureProperties(feature: Feature) {
        if (feature.collectionId) {
            const collection = this.app.state.collections[feature.collectionId];
            if (collection) {
                return {
                    ...collection.properties,
                    ...feature.properties,
                };
            }
        }
        return feature.properties;
    }

    private featureStyle<T>(feature: Feature, key: string, fallback: T): T {
        const properties = this.featureProperties(feature);

        if (key in properties) {
            return properties[key as keyof typeof properties] as T;
        } else {
            return fallback;
        }
    }

    private getNodeColor(featureId: string, index: number): string {
        const feature = this.getFeature(featureId);
        if (!feature) return MEASUREMENT_COLOR;

        if (this.isFeatureSelected(featureId) && !this.isStylePreviewing(featureId)) {
            if (this.isCrossSection(feature)) {
                return index === 0 ? CROSS_SECTION_START_COLOR : CROSS_SECTION_END_COLOR;
            }
            return MEASUREMENT_HIGHLIGHT_COLOR;
        }

        return this.featureStyle<string>(feature, "marker-color", MEASUREMENT_COLOR);
    }

    private getLineColor(featureId: string, feature: Feature): string {
        if (this.isFeatureSelected(featureId) && !this.isStylePreviewing(featureId)) {
            if (this.isCrossSection(feature)) {
                return CROSS_SECTION_START_COLOR;
            }
            return MEASUREMENT_HIGHLIGHT_COLOR;
        }

        return this.featureStyle<string>(feature, "stroke", MEASUREMENT_COLOR);
    }

    private getLineWidth(featureId: string, feature: Feature): number {
        const isDragging = this.state.dragNodeIndex !== null && this.state.dragFeatureId !== null;
        const isSelected = featureId === this.selectionId;

        if (isSelected || isDragging) {
            return LINE_WIDTH;
        }

        if (this.isFeatureHovered(featureId)) {
            return HOVER_LINE_WIDTH;
        }

        if (isLineStringFeature(feature) || isPolygonFeature(feature)) {
            const sw = feature.properties["stroke-width"];
            if (typeof sw === "number") return sw;
        }

        return LINE_WIDTH;
    }

    private getMarkerOpacity(feature: Feature): number {
        return this.featureStyle<number>(feature, "marker-opacity", 1);
    }

    private getStrokeOpacity(feature: Feature): number {
        return this.featureStyle<number>(feature, "stroke-opacity", 1);
    }

    private getMarkerSize(feature: Feature): number {
        switch (this.featureStyle<string>(feature, "marker-size", "medium")) {
            case "small":
                return SMALL_POINT_SIZE;
            case "large":
                return LARGE_POINT_SIZE;
            case "medium":
            default:
                return POINT_SIZE;
        }
    }

    private lastPosition = new THREE.Vector3();
    private firstPosition = new THREE.Vector3();
    private getNodeSize(featureId: string, index: number): number {
        const feature = this.getFeature(featureId);
        if (!feature) return POINT_SIZE;

        // Highlight the last node when snapped to the first one
        const positions = featurePositions(feature);
        const isLast = index === positions.length - 1;
        const canClose = this.canCloseDraft(feature, positions);
        if (canClose && isLast) {
            this.firstPosition.fromArray(positions[0]);
            this.lastPosition.fromArray(positions[index]);

            if (this.lastPosition.equals(this.firstPosition)) {
                return LARGE_POINT_SIZE;
            }
        }

        if (featureId === this.selectionId || featureId === this.state.dragFeatureId) {
            return this.getMarkerSize(feature);
        }

        if (this.isFeatureHovered(featureId)) {
            return LARGE_POINT_SIZE;
        }

        return this.getMarkerSize(feature);
    }

    private anchorTemp = new THREE.Vector3();
    private getAnchor(vertices: number[]): THREE.Vector3 {
        return this.anchorTemp.set(vertices[0], vertices[1], vertices[2]);
    }

    private subtractAnchor(vertices: number[], anchor: THREE.Vector3): number[] {
        for (let i = 0; i < vertices.length; i += 3) {
            vertices[i] -= anchor.x;
            vertices[i + 1] -= anchor.y;
            vertices[i + 2] -= anchor.z;
        }
        return vertices;
    }

    private updatePoints(featureId: string) {
        const feature = this.getFeature(featureId);
        if (!feature || !feature.geometry) return;

        const objects = this.promoted.get(featureId);
        if (!objects) return;

        const vertices = this.getVertices(featureId);
        const anchor = this.getAnchor(vertices);
        const vertexCount = vertices.length / 3;
        const isDrafting = this.state.input === "drafting" && this.selectionId === featureId;

        // While drafting, the in-progress node (the last one) is split out onto
        // `draftPoints` (draft layer) so the raycaster cannot snap onto itself.
        const committedCount = isDrafting ? Math.max(0, vertexCount - 1) : vertexCount;
        const committedVertices = this.subtractAnchor(vertices.slice(0, committedCount * 3), anchor);
        const position = new THREE.Float32BufferAttribute(committedVertices, 3);
        const size = this.getNodeSize(featureId, 0);

        // Per-node colors (e.g. green start / red end for a selected cross-section).
        const colors: number[] = [];
        const tmpColor = new THREE.Color();
        for (let i = 0; i < committedCount; i++) {
            tmpColor.set(this.getNodeColor(featureId, i));
            colors.push(tmpColor.r, tmpColor.g, tmpColor.b);
        }
        const color = new THREE.Float32BufferAttribute(colors, 3);

        const opacity = this.getMarkerOpacity(feature);

        objects.points.visible = true;
        objects.points.position.copy(anchor);
        objects.points.updateMatrixWorld();
        objects.points.geometry.setAttribute("position", position);
        objects.points.geometry.setAttribute("color", color);
        objects.points.geometry.computeBoundingSphere();
        objects.points.material = getPointMaterial(size, opacity);

        if (isDrafting && vertexCount > 0) {
            const draftIndex = vertexCount - 1;
            const draftVertices = this.subtractAnchor(vertices.slice(draftIndex * 3), anchor);
            const position = new THREE.Float32BufferAttribute(draftVertices, 3);
            const color = this.getNodeColor(featureId, draftIndex);
            const size = this.getNodeSize(featureId, draftIndex);

            objects.draftPoints.visible = true;
            objects.draftPoints.position.copy(anchor);
            objects.draftPoints.updateMatrixWorld();
            objects.draftPoints.geometry.setAttribute("position", position);
            objects.draftPoints.geometry.computeBoundingSphere();
            objects.draftPoints.material = getPointMaterial(size, opacity, color);
        } else {
            objects.draftPoints.visible = false;
        }
    }

    public triangleArea(triangle: number[], vertices: number[]) {
        const a = new THREE.Vector3().fromArray(vertices.slice(triangle[0] * 3, triangle[0] * 3 + 3));
        const b = new THREE.Vector3().fromArray(vertices.slice(triangle[1] * 3, triangle[1] * 3 + 3));
        const c = new THREE.Vector3().fromArray(vertices.slice(triangle[2] * 3, triangle[2] * 3 + 3));

        const ab = b.clone().sub(a);
        const ac = c.clone().sub(a);

        const cross = ab.clone().cross(ac);
        return cross.length() / 2;
    }

    private getFillColor(featureId: string, feature: Feature): string {
        const lineColor = this.getLineColor(featureId, feature);
        if (!this.isFeatureSelected(featureId) || this.isStylePreviewing(featureId)) {
            return this.featureStyle<string>(feature, "fill", lineColor);
        }

        return lineColor;
    }

    private updateFill(featureId: string) {
        const feature = this.getFeature(featureId);
        if (!feature || !isPolygonFeature(feature)) return;

        const objects = this.promoted.get(featureId);
        if (!objects) return;

        const vertices = this.getVertices(featureId);
        if (vertices.length < 9 || vertices.length % 3 !== 0) {
            objects.fill.visible = false;
            return;
        }

        const opacity = this.featureStyle<number>(feature, "fill-opacity", FILL_OPACITY);
        const color = this.getFillColor(featureId, feature);

        const anchor = this.getAnchor(vertices);
        const localVertices = this.subtractAnchor(vertices.slice(), anchor);

        objects.fill.visible = true;
        objects.fill.position.copy(anchor);
        objects.fill.updateMatrixWorld();
        const position = new THREE.Float32BufferAttribute(localVertices, 3);
        const index = triangulate(localVertices);
        objects.fill.geometry.setAttribute("position", position);
        objects.fill.geometry.setIndex(index);

        const isCrop = isPolygonFeature(feature) && feature.properties.crop === true;
        objects.fill.material = isCrop ? getCropFillMaterial(color, opacity) : getFillMaterial(color, opacity);
    }

    private behindCamera(camera: THREE.Camera, points: THREE.Vector3[]): boolean {
        for (const point of points) {
            const view = point.clone().applyMatrix4(camera.matrixWorldInverse);
            if (view.z > 0) {
                return true;
            }
        }
        return false;
    }

    private getScreenHeight(start: THREE.Vector3, end: THREE.Vector3): number {
        const startNDC = start.clone().project(this.context.camera);
        const endNDC = end.clone().project(this.context.camera);
        return Math.abs(startNDC.y - endNDC.y) * 0.5 * this.context.size.height;
    }

    private getScreenWidth(start: THREE.Vector3, end: THREE.Vector3): number {
        const startNDC = start.clone().project(this.context.camera);
        const endNDC = end.clone().project(this.context.camera);
        return Math.abs(startNDC.x - endNDC.x) * 0.5 * this.context.size.width;
    }

    private getLabelScreenSize(label: CSS2DObject): { width: number; height: number } {
        // Size depends only on the text content.
        const key = label.element.textContent ?? "";
        const cached = this.labelSizes.get(key);
        if (cached) return cached;

        document.body.appendChild(label.element);
        label.element.style.visibility = "hidden";
        const width = label.element.offsetWidth;
        const height = label.element.offsetHeight;
        document.body.removeChild(label.element);
        label.element.style.visibility = "visible";

        const size = { width, height };
        this.labelSizes.set(key, size);
        return size;
    }

    private getLabel(
        position: THREE.Vector3,
        text: string,
    ): { object: CSS2DObject; width: number; height: number } | null {
        if (this.behindCamera(this.context.camera, [position])) return null;

        const element = document.createElement("div");
        element.textContent = text;
        element.className = [
            "bg-white",
            "text-gray-90",
            "px-2",
            "py-1",
            "rounded-md",
            "text-xs",
            "shadow",
            "absolute",
            "css2d-label",
        ].join(" ");

        const label = new CSS2DObject(element);
        label.position.copy(position);
        label.layers.set(LAYERS.annotations);

        const screenSize = this.getLabelScreenSize(label);
        return { object: label, width: screenSize.width, height: screenSize.height };
    }

    private getLineLabel(
        start: THREE.Vector3,
        end: THREE.Vector3,
    ): { object: CSS2DObject; width: number; height: number } | null {
        const position = start.clone().lerp(end, 0.5);
        const text = humanDistance(start.distanceTo(end), this.app.displayUnits(this.prefs.state.units));
        const label = this.getLabel(position, text);
        if (!label) return null;

        // Hide the label when the segment is shorter on screen than the label itself.
        const screenWidth = this.getScreenWidth(start, end);
        const screenHeight = this.getScreenHeight(start, end);
        if (screenWidth < label.width && screenHeight < label.height) return null;

        return label;
    }

    private getAreaLabel(
        center: THREE.Vector3,
        area: number,
    ): { object: CSS2DObject; width: number; height: number } | null {
        const text = humanArea(area, this.app.displayUnits(this.prefs.state.units));
        return this.getLabel(center, text);
    }

    private lablesOverlap(
        label1: { object: CSS2DObject; width: number; height: number },
        label2: { object: CSS2DObject; width: number; height: number },
    ): boolean {
        const ndc1 = label1.object.position.clone().project(this.context.camera);
        const ndc2 = label2.object.position.clone().project(this.context.camera);

        const x1 = (ndc1.x * 0.5 + 0.5) * this.context.size.width;
        const y1 = (ndc1.y * 0.5 + 0.5) * this.context.size.height;
        const x2 = (ndc2.x * 0.5 + 0.5) * this.context.size.width;
        const y2 = (ndc2.y * 0.5 + 0.5) * this.context.size.height;

        const box1 = new THREE.Box2(
            new THREE.Vector2(x1 - label1.width / 2, y1 - label1.height / 2),
            new THREE.Vector2(x1 + label1.width / 2, y1 + label1.height / 2),
        );
        const box2 = new THREE.Box2(
            new THREE.Vector2(x2 - label2.width / 2, y2 - label2.height / 2),
            new THREE.Vector2(x2 + label2.width / 2, y2 + label2.height / 2),
        );

        return box1.intersectsBox(box2);
    }

    private addLabel(parts: FeatureParts, label: { object: CSS2DObject }) {
        this.labels.add(label.object);
        parts.labels.push(label.object);
    }

    private clearLabels(featureId: string) {
        const objects = this.promoted.get(featureId);
        if (!objects) return;
        for (const label of objects.labels) {
            this.labels.remove(label);
        }
        objects.labels = [];
    }

    private updateLabels(featureId: string) {
        this.clearLabels(featureId);

        const feature = this.getFeature(featureId);
        if (!feature || !feature.geometry) return;

        const objects = this.promoted.get(featureId);
        if (!objects) return;

        // Show measurement labels for selected features and title otherwise if configured
        if (featureId === this.selectionId) {
            this.updateMeasurementLabels(featureId, feature, objects);
        } else if (feature.properties?.label) {
            this.updateFeatureLabel(featureId, feature, objects);
        }
    }

    private updateMeasurementLabels(featureId: string, feature: Feature, objects: FeatureParts) {
        if (isLineStringFeature(feature)) {
            const coords = feature.geometry.coordinates;
            const isDrafting = this.state.input === "drafting" && this.selectionId === featureId;

            if (coords.length === 2 && !isDrafting) {
                const start = new THREE.Vector3().fromArray(this.toLocalVec(coords[0]));
                const end = new THREE.Vector3().fromArray(this.toLocalVec(coords[1]));
                const label = this.getLineLabel(start, end);
                if (!label) return;
                this.addLabel(objects, label);

                // Height/ground decomposition: an L-shaped path low -> corner -> high.
                const high = end.z > start.z ? end : start;
                const low = end.z <= start.z ? end : start;
                const corner = new THREE.Vector3(high.x, high.y, low.z);

                const heightLabel = this.getLineLabel(high, corner);
                const groundLabel = this.getLineLabel(low, corner);

                if (heightLabel && groundLabel) {
                    const heightOverlap = this.lablesOverlap(heightLabel, label);
                    const groundOverlap = this.lablesOverlap(groundLabel, label);

                    if (!heightOverlap && !groundOverlap) {
                        let opacity = 1;
                        if ("stroke-opacity" in feature.properties) {
                            opacity = feature.properties["stroke-opacity"] ?? 1;
                        }

                        const color = this.getLineColor(featureId, feature);
                        const lineWidth = this.getLineWidth(featureId, feature);
                        const anchor = this.getAnchor(this.getVertices(featureId));
                        const points = [low, corner, high];
                        const dashSize = this.getDashSize(points);

                        this.setAuxLine(objects, points, anchor, color, lineWidth, opacity, dashSize);
                        this.addLabel(objects, heightLabel);
                        this.addLabel(objects, groundLabel);
                    }
                }
            } else {
                for (let i = 1; i < coords.length; i++) {
                    const start = new THREE.Vector3().fromArray(this.toLocalVec(coords[i - 1]));
                    const end = new THREE.Vector3().fromArray(this.toLocalVec(coords[i]));
                    const label = this.getLineLabel(start, end);
                    if (label) this.addLabel(objects, label);
                }
            }
        } else if (isPolygonFeature(feature)) {
            const centroid = this.polygonCentroid(this.getVertices(featureId));
            if (centroid) {
                const label = this.getAreaLabel(centroid.center, centroid.area);
                if (label) this.addLabel(objects, label);
            }
        }
    }

    // Area-weighted centroid of a triangulated polygon, in local scene coordinates.
    private polygonCentroid(vertices: number[]): { center: THREE.Vector3; area: number } | null {
        if (vertices.length < 9 || vertices.length % 3 !== 0) return null;

        const triangles = triangulate(vertices);
        let totalArea = 0;
        const center = new THREE.Vector3();
        for (let i = 0; i < triangles.length; i += 3) {
            const tri = [triangles[i], triangles[i + 1], triangles[i + 2]];
            const area = this.triangleArea(tri, vertices);
            const a = new THREE.Vector3().fromArray(vertices, tri[0] * 3);
            const b = new THREE.Vector3().fromArray(vertices, tri[1] * 3);
            const c = new THREE.Vector3().fromArray(vertices, tri[2] * 3);
            center.add(a.add(b).add(c).divideScalar(3).multiplyScalar(area));
            totalArea += area;
        }

        if (totalArea <= 0) return null;
        center.divideScalar(totalArea);
        return { center, area: totalArea };
    }

    // The feature's title, falling back to its generic kind name when untitled.
    private getTitleText(feature: Feature): string {
        const title = feature.properties?.title;
        if (typeof title === "string" && title.trim().length > 0) return title.trim();
        return featureDisplay(feature).name;
    }

    private featureLabelPosition = new THREE.Vector3();
    private featureLabelEnd = new THREE.Vector3();
    private featureLabelCenter = new THREE.Vector2(0.5, 1.35);
    private updateFeatureLabel(featureId: string, feature: Feature, objects: FeatureParts) {
        const text = this.getTitleText(feature);

        if (isPointFeature(feature)) {
            const coords = feature.geometry.coordinates;
            if (coords.length === 0) return;
            this.featureLabelPosition.fromArray(this.toLocalVec(coords));
        } else if (isLineStringFeature(feature)) {
            const coords = feature.geometry.coordinates;
            if (coords.length < 2) return;

            const segment = Math.floor((coords.length - 1) / 2);
            this.featureLabelPosition.fromArray(this.toLocalVec(coords[segment]));
            this.featureLabelEnd.fromArray(this.toLocalVec(coords[segment + 1]));

            this.featureLabelPosition.lerp(this.featureLabelEnd, 0.5);
        } else if (isPolygonFeature(feature)) {
            const vertices = this.getVertices(featureId);
            const centroid = this.polygonCentroid(vertices);
            if (!centroid) return;

            this.featureLabelPosition.copy(centroid.center);
        } else {
            return;
        }

        const label = this.getLabel(this.featureLabelPosition, text);
        if (!label) return;

        // Point features use "center" to offset the label above the marker.
        if (isPointFeature(feature)) {
            label.object.center.copy(this.featureLabelCenter);
        }

        this.addLabel(objects, label);
    }

    private reconcilePromotion(featureId: string) {
        const feature = this.getFeature(featureId);
        if (!feature) return;

        const labelEnabled = feature.geometry && feature.properties?.label;
        const isSelected = featureId === this.selectionId;
        const isHovered = this.isFeatureHovered(featureId);
        const shouldPromote = isSelected || isHovered || labelEnabled;

        if (shouldPromote) {
            this.promote(featureId);
        } else {
            this.demote(featureId);
        }
    }

    public update(ids: string[] = []) {
        const featureIds = new Set<string>();
        for (const id of ids) {
            if (this.app.state.collections[id]) {
                for (const memberId of this.getFeatureIds(id)) {
                    featureIds.add(memberId);
                }
            } else {
                featureIds.add(id);
            }
        }

        for (const id of featureIds) {
            this.syncFeatureCell(id);

            const feature = this.getFeature(id);
            if (!feature) continue;

            this.reconcilePromotion(id);

            const properties = feature.properties;
            if (properties?.hidden) {
                this.hideObjects(id);
                continue;
            }

            this.updatePoints(id);
            if (!isPointFeature(feature)) {
                this.updateLine(id);
                this.updateFill(id);
            }

            this.updateLabels(id);
        }

        this.state.revision++;
        this.rebuildGeometry();
        this.context.update();
    }

    public addFeatures(features: Record<string, Feature>) {
        const labeledIds: string[] = [];
        for (const [id, feature] of Object.entries(features)) {
            feature.id = id;
            if (typeof feature.collectionId === "string") {
                // Ignore tiles that resolve after collection has been deleted
                if (!this.app.state.collections[feature.collectionId]) {
                    continue;
                }

                this.collectionFeatures.set(id, feature);
            } else {
                this.state.features[id] = feature;
            }

            this.invalidate(id);
            this.syncFeatureCell(id);

            // Title-labeled features must be promoted to anchor their label.
            if (feature.geometry && feature.properties?.label) {
                labeledIds.push(id);
            }
        }

        // update() promotes the labeled features and rebuilds all dirty cells.
        if (labeledIds.length > 0) {
            this.update(labeledIds);
        } else {
            this.rebuildGeometry();
            this.context.update();
        }
    }

    private geometryToFeatures(
        geometry: GeoJSON.Geometry,
        properties: GeoJSON.GeoJsonProperties,
    ): Exclude<Feature, PropertiesFeature>[] {
        if (geometry === null) return [];

        switch (geometry.type) {
            case "Point":
                return [
                    {
                        type: "Feature",
                        geometry: geometry,
                        properties: { "dataset-id": null, ...properties },
                    },
                ];
            case "LineString":
                return [
                    {
                        type: "Feature",
                        geometry: openedRing(geometry) as LineStringFeature["geometry"],
                        properties: properties as LineStringFeature["properties"],
                    },
                ];
            case "Polygon":
                return [
                    {
                        type: "Feature",
                        geometry: openedRing(geometry) as PolygonFeature["geometry"],
                        properties: properties as PolygonFeature["properties"],
                    },
                ];
            case "MultiPoint":
                return geometry.coordinates.flatMap((coordinates) =>
                    this.geometryToFeatures({ type: "Point", coordinates }, properties),
                );
            case "MultiLineString":
                return geometry.coordinates.flatMap((coordinates) =>
                    this.geometryToFeatures({ type: "LineString", coordinates }, properties),
                );
            case "MultiPolygon":
                return geometry.coordinates.flatMap((coordinates) =>
                    this.geometryToFeatures({ type: "Polygon", coordinates }, properties),
                );
            default:
                return geometry.geometries.flatMap((child) => this.geometryToFeatures(child, properties));
        }
    }

    public async importGeoJsonCollection(
        name: string,
        data: GeoJSON.FeatureCollection,
        sourceAssetIds: string[] = [],
    ): Promise<boolean> {
        const projectId = this.project.get().project_id;

        const features: Feature[] = [];
        for (const input of data.features) {
            const properties = (input.properties ?? {}) as Feature["properties"];
            if (input.geometry === null) {
                features.push({ type: "Feature", geometry: null, properties });
            } else {
                for (const feature of this.geometryToFeatures(input.geometry, properties)) {
                    if (!isPointFeature(feature)) {
                        feature.properties["marker-opacity"] = 0;
                    }
                    features.push(feature);
                }
            }
        }

        if (features.length === 0) return false;
        this.journal.write();

        const collection = await this.project.createCollection(
            projectId,
            name || "Collection",
            features,
            { locked: true },
            sourceAssetIds,
        );
        if (!collection) return false;

        this.app.state.collections[collection.collection_id] = collection;
        await this.app.loadDatasets();
        this.eval();
        return true;
    }
}
